import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  IconButton,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNotify } from '@/stores/notificationStore';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useAuthStore } from '@/stores/authStore';
import {
  useApproveWorkPathTask,
  useAutoCreateWorkPath,
  useBookingWorkPaths,
  useCreateBookingWorkPath,
  useFinalApproveWorkPath,
  useDeleteBookingWorkPath,
  useWorkPathTemplates,
  type BookingWorkPath,
  type WorkPathStatus,
  type WorkPathTask,
} from '@/hooks/useWorkPaths';
import { WorkPathTaskDialog } from './WorkPathTaskDialog';
import { formatDate } from '@/utils/locale';
import { describeApiError } from '@/lib/describeApiError';

interface Props {
  workOrderId: string;
  /**
   * The booking's services, used to count templates that would match before
   * offering the one-click button (#2476). Optional so the section still
   * renders where the caller does not know them; the button simply stays
   * hidden, and picking a template by hand still works.
   */
  serviceIds?: string[];
  cardVariant?: 'elevation' | 'outlined';
}

const statusColor: Record<WorkPathStatus, 'default' | 'info' | 'success'> = {
  pending: 'default',
  in_progress: 'info',
  completed: 'success',
};

export function WorkPathSection({ workOrderId, serviceIds = [], cardVariant }: Props) {
  const { t } = useTranslation('workpaths');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();

  const { data: workPaths = [], isLoading } = useBookingWorkPaths(workOrderId);
  const { data: templates = [] } = useWorkPathTemplates();
  const createWorkPath = useCreateBookingWorkPath();
  const autoCreate = useAutoCreateWorkPath();
  const approveTask = useApproveWorkPathTask();
  const finalApprove = useFinalApproveWorkPath();

  /**
   * Deliberately `inspections.approve`, not the `checklists.manage` a mechanic
   * carries — otherwise they would sign off their own work, which is the one
   * thing supervisor approval exists to prevent (work-paths.ts:1099).
   */
  const canApprove = useAuthStore((s) => s.hasPermission('inspections.approve'));

  const handleApproveTask = async (workPathId: string, taskId: string) => {
    try {
      await approveTask.mutateAsync({ id: workPathId, taskId, bookingId: workOrderId });
      notify.success(t('task.approve'));
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('errors.approveTaskFailed') }));
    }
  };

  const handleFinalApprove = async (workPathId: string) => {
    try {
      await finalApprove.mutateAsync({ id: workPathId, bookingId: workOrderId });
      notify.success(t('task.finalApproved'));
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('errors.finalApproveFailed') }));
    }
  };
  const deleteWorkPath = useDeleteBookingWorkPath();

  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [openTask, setOpenTask] = useState<{ workPathId: string; task: WorkPathTask } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BookingWorkPath | null>(null);

  const hasWorkPath = workPaths.length > 0;

  /**
   * How many templates this booking would match. Mirrors the checklist card
   * (ServiceBookChecklistsCard.tsx:142) so the two behave alike.
   *
   * The server also matches on vehicle make (work-paths.ts:834), which is not
   * known here, so this can undercount — the button hides where the server
   * would have found something. That is the safe direction: a button that
   * promises a match and delivers none is worse than one that stays hidden
   * while hand-picking remains available.
   */
  const matchingTemplateCount = useMemo(
    () =>
      templates.filter((tmpl) => {
        if (tmpl.isDefault) return true;
        if (tmpl.serviceIds && tmpl.serviceIds.length > 0) {
          return tmpl.serviceIds.some((sid) => serviceIds.includes(sid));
        }
        return false;
      }).length,
    [templates, serviceIds],
  );

  const handleAutoCreate = async () => {
    try {
      const result = await autoCreate.mutateAsync(workOrderId);
      if (result.created) {
        notify.success(t('empty.autoCreated'));
      } else if (result.data == null) {
        // Not a failure: the booking's services simply match no template.
        notify.info(t('empty.autoCreatedNone'));
      }
      // Already had one — the list refreshes and says so; no toast needed.
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('errors.autoCreateFailed') }));
    }
  };

  const handleCreate = async () => {
    if (!selectedTemplateId) return;
    try {
      await createWorkPath.mutateAsync({
        bookingId: workOrderId,
        templateId: selectedTemplateId,
      });
      notify.success(t('created'));
      setSelectedTemplateId('');
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('errors.createFailed') }));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteWorkPath.mutateAsync({ id: deleteTarget.id, bookingId: workOrderId });
      notify.success(t('deleted'));
      setDeleteTarget(null);
    } catch (error) {
      notify.error(tc('error'), describeApiError(error, tc, { fallback: t('errors.deleteFailed') }));
    }
  };

  return (
    <>
      <Card variant={cardVariant} sx={{ mb: 3 }} data-testid="work-path-section">
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <AccountTreeIcon color="primary" />
            <Typography variant="h6">{t('title')}</Typography>
          </Box>

          {isLoading && <LinearProgress sx={{ mb: 2 }} />}

          {/* Creation view — no work path yet */}
          {!isLoading && !hasWorkPath && (
            <Box data-testid="work-path-empty">
              <Typography variant="body2" color="text.secondary" mb={1.5}>
                {t('empty.description')}
              </Typography>
              {/*
                No templates at all was a disabled menu row — a dead end that
                named the problem without offering a way out (#2476). Send the
                workshop where the templates are made instead.
              */}
              {templates.length === 0 ? (
                <Button
                  variant="outlined"
                  size="small"
                  component={RouterLink}
                  to="/settings?tab=work-paths"
                  data-testid="work-path-no-templates"
                >
                  {t('empty.createTemplate')}
                </Button>
              ) : (
                <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                  {/*
                    One click when the booking's own services already say which
                    template applies; the hand-pick below stays for the rest.
                  */}
                  {matchingTemplateCount > 0 && (
                    <Tooltip title={t('empty.autoCreateHint', { count: matchingTemplateCount })}>
                      <Button
                        variant="contained"
                        startIcon={<AutoFixHighIcon />}
                        onClick={() => void handleAutoCreate()}
                        disabled={autoCreate.isPending}
                        data-testid="work-path-auto-create"
                      >
                        {t('empty.autoCreate')}
                      </Button>
                    </Tooltip>
                  )}
                  <TextField
                    select
                    label={t('empty.selectTemplate')}
                    value={selectedTemplateId}
                    onChange={(e) => setSelectedTemplateId(e.target.value)}
                    size="small"
                    sx={{ minWidth: 220 }}
                    data-testid="work-path-template-select"
                  >
                    {templates.map((tmpl) => (
                      <MenuItem key={tmpl.id} value={tmpl.id}>
                        {tmpl.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <Button
                    variant={matchingTemplateCount > 0 ? 'outlined' : 'contained'}
                    onClick={() => void handleCreate()}
                    disabled={!selectedTemplateId || createWorkPath.isPending}
                  >
                    {t('empty.create')}
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {/* Work paths exist — overall progress + task cards */}
          {!isLoading &&
            workPaths.map((workPath) => {
              const overall =
                workPath.totalTasks > 0
                  ? Math.round((workPath.completedTasks / workPath.totalTasks) * 100)
                  : 0;
              const wpComplete = workPath.status === 'completed';

              return (
                <Box key={workPath.id} mb={2} data-testid="work-path-instance">
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {workPath.name}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Chip
                        size="small"
                        label={t(`status.${workPath.status}`)}
                        color={statusColor[workPath.status]}
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                      <Tooltip title={tc('delete')}>
                        <IconButton
                          size="small"
                          aria-label={tc('delete')}
                          onClick={() => setDeleteTarget(workPath)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                    <LinearProgress
                      variant="determinate"
                      value={overall}
                      color={wpComplete ? 'success' : 'primary'}
                      sx={{ flex: 1, borderRadius: 1, height: 6 }}
                    />
                    <Typography variant="caption" color="text.secondary" whiteSpace="nowrap">
                      {t('overallProgress', {
                        completed: workPath.completedTasks,
                        total: workPath.totalTasks,
                      })}
                    </Typography>
                  </Box>

                  <Stack spacing={1}>
                    {workPath.tasks.map((task) => (
                      <Box
                        key={task.id}
                        data-testid="work-path-task-card"
                        sx={{
                          p: 1.5,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' },
                        }}
                        onClick={() => setOpenTask({ workPathId: workPath.id, task })}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                          <Typography variant="body2" fontWeight={600}>
                            {task.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            whiteSpace="nowrap"
                          >
                            {t('task.verificationCount', { count: task.verifications.length })}
                          </Typography>
                        </Box>

                        {/*
                          Every check is done but the task is not: it needs a
                          supervisor. Until #2386 got a UI this state existed
                          only in the data, so the path sat at in_progress and
                          nobody could see why (rollup: isTaskDone excludes it).
                        */}
                        {task.status === 'awaiting_approval' && (
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1}
                            mt={1}
                            flexWrap="wrap"
                          >
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label={t('task.awaitingApproval')}
                            />
                            {canApprove && (
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                disabled={approveTask.isPending}
                                onClick={(e) => {
                                  // The card opens the task dialog on click.
                                  e.stopPropagation();
                                  void handleApproveTask(workPath.id, task.id);
                                }}
                                data-testid={`approve-task-${task.id}`}
                              >
                                {t('task.approve')}
                              </Button>
                            )}
                          </Box>
                        )}

                        {task.status === 'approved' && task.supervisorApprovedAt && (
                          <Typography variant="caption" color="success.main" display="block" mt={0.5}>
                            {t('task.approvedBy', {
                              at: formatDate(task.supervisorApprovedAt),
                            })}
                          </Typography>
                        )}
                        {task.description && (
                          <Typography variant="caption" color="text.secondary">
                            {task.description}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Stack>

                  {/*
                    Handover check for the whole path, separate from per-task
                    approval: every task is done, the supervisor confirms the
                    car can be released. Offered only once nothing is still
                    waiting, so it cannot skip an unapproved task.
                  */}
                  {canApprove &&
                    wpComplete &&
                    !workPath.tasks.some((task) => task.status === 'awaiting_approval') && (
                      <Box mt={1.5}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          disabled={finalApprove.isPending}
                          onClick={() => void handleFinalApprove(workPath.id)}
                          data-testid={`final-approve-${workPath.id}`}
                        >
                          {t('task.finalApprove')}
                        </Button>
                      </Box>
                    )}
                </Box>
              );
            })}
        </CardContent>
      </Card>

      <WorkPathTaskDialog
        workPathId={openTask?.workPathId ?? ''}
        bookingId={workOrderId}
        task={openTask?.task ?? null}
        onClose={() => setOpenTask(null)}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => void handleDelete()}
        title={tc('delete')}
        message={t('deleteConfirm', { name: deleteTarget?.name ?? '' })}
      />
    </>
  );
}
